import { useState, useEffect, useCallback } from 'react';
import type { RecentItem } from './types';

const STORAGE_KEY = 'globalCommandPalette:recentItems';
const MAX_RECENT_ITEMS = 10;

export function useRecentItems() {
    const [recentItems, setRecentItems] = useState<RecentItem[]>([]);

    // Load from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Convert timestamp strings back to Date objects
                const items = parsed.map((item: any) => ({
                    ...item,
                    timestamp: new Date(item.timestamp),
                }));
                setRecentItems(items);
            }
        } catch (error) {
            console.error('Failed to load recent items:', error);
        }
    }, []);

    // Save to localStorage whenever items change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(recentItems));
        } catch (error) {
            console.error('Failed to save recent items:', error);
        }
    }, [recentItems]);

    const addRecentItem = useCallback((item: Omit<RecentItem, 'timestamp' | 'frequency'>) => {
        setRecentItems((prev) => {
            // Check if item already exists
            const existingIndex = prev.findIndex((i) => i.id === item.id && i.type === item.type);

            let updatedItems: RecentItem[];

            if (existingIndex !== -1) {
                // Update existing item: increase frequency, update timestamp
                updatedItems = [...prev];
                updatedItems[existingIndex] = {
                    ...updatedItems[existingIndex],
                    timestamp: new Date(),
                    frequency: (updatedItems[existingIndex].frequency || 1) + 1,
                };
            } else {
                // Add new item
                const newItem: RecentItem = {
                    ...item,
                    timestamp: new Date(),
                    frequency: 1,
                };
                updatedItems = [newItem, ...prev];
            }

            // Sort by: frequency (40%) + recency (60%)
            updatedItems.sort((a, b) => {
                const now = Date.now();
                const aRecency = now - a.timestamp.getTime();
                const bRecency = now - b.timestamp.getTime();

                const aFreq = a.frequency || 1;
                const bFreq = b.frequency || 1;

                // Normalize scores (lower recency is better)
                const aScore = (aFreq * 0.4) - (aRecency / (1000 * 60 * 60)) * 0.6; // Recency in hours
                const bScore = (bFreq * 0.4) - (bRecency / (1000 * 60 * 60)) * 0.6;

                return bScore - aScore;
            });

            // Limit to MAX_RECENT_ITEMS
            return updatedItems.slice(0, MAX_RECENT_ITEMS);
        });
    }, []);

    const clearRecentItems = useCallback(() => {
        setRecentItems([]);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    return {
        recentItems,
        addRecentItem,
        clearRecentItems,
    };
}
